import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { fetchPostBySlug } from '../api/pages'
import { BalloonLayer } from '../components/ui/BalloonSVG'

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
}

function formatDate(dateString) {
  if (!dateString) return null
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  })
}

export default function DynamicPost() {
  const { slug } = useParams()

  const { data: post, isLoading, isError } = useQuery({
    queryKey: ['post', slug],
    queryFn: () => fetchPostBySlug(slug),
  })

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20 bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="font-body text-ink-muted text-sm">Loading post…</p>
        </div>
      </div>
    )
  }

  if (isError || !post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pt-20 text-center px-4 bg-background">
        <p className="text-7xl mb-6">🎈</p>
        <h1 className="font-display text-4xl text-ink mb-3">Post Not Found</h1>
        <p className="text-ink-muted font-body mb-8">This post doesn't exist or was removed.</p>
        <Link
          to="/"
          className="inline-block bg-primary text-white font-body font-semibold px-6 py-3 rounded-full hover:bg-primary-dark transition-colors duration-200"
        >
          ← Back to Home
        </Link>
      </div>
    )
  }

  // Strip HTML tags from excerpt for the hero subtitle
  const excerpt = post.excerpt?.rendered
    ? post.excerpt.rendered.replace(/<[^>]*>/g, '').trim()
    : null

  // Featured image from _embed
  const featuredImage =
    post._embedded?.['wp:featuredmedia']?.[0]?.source_url ?? null

  // Categories from _embed
  const categories =
    post._embedded?.['wp:term']?.[0]?.map((c) => c.name) ?? []

  return (
    <>
      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section
        className="relative pt-32 pb-20 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #FF6BB5 0%, #7C3AFF 100%)' }}
      >
        <div className="absolute bottom-0 left-1/3 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none" />
        <BalloonLayer opacity={0.28} />

        <div className="container-custom relative z-10 text-center max-w-3xl">
          {/* Eyebrow — date + categories */}
          <motion.p
            variants={itemVariants}
            initial="hidden"
            animate="show"
            className="inline-flex items-center gap-2 mb-5 px-5 py-2 rounded-full bg-white/20 border border-white/30 text-white text-sm font-body font-bold tracking-widest uppercase backdrop-blur-sm"
          >
            ✍️{categories.length > 0 ? ` ${categories.join(' · ')}` : ' Blog'}
            {formatDate(post.date) && (
              <span className="font-normal normal-case tracking-normal opacity-80">
                — {formatDate(post.date)}
              </span>
            )}
          </motion.p>

          {/* Title */}
          <motion.h1
            variants={itemVariants}
            initial="hidden"
            animate="show"
            transition={{ delay: 0.1 }}
            className="font-display text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6"
            dangerouslySetInnerHTML={{ __html: post.title.rendered }}
          />

          {/* Excerpt / description */}
          {excerpt && (
            <motion.p
              variants={itemVariants}
              initial="hidden"
              animate="show"
              transition={{ delay: 0.2 }}
              className="font-body text-white/85 text-lg leading-relaxed max-w-2xl mx-auto"
            >
              {excerpt}
            </motion.p>
          )}
        </div>
      </section>

      {/* ── Featured image ───────────────────────────────────────────── */}
      {featuredImage && (
        <div className="bg-background">
          <div className="container-custom max-w-3xl -mt-12 relative z-10">
            <img
              src={featuredImage}
              alt={post.title.rendered.replace(/<[^>]*>/g, '')}
              className="w-full rounded-3xl shadow-card-hover object-cover max-h-[480px]"
            />
          </div>
        </div>
      )}

      {/* ── Post content ─────────────────────────────────────────────── */}
      <section className="bg-background py-16 md:py-24">
        <div className="container-custom max-w-3xl">
          <motion.div
            variants={itemVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-60px' }}
            className="
              font-body text-ink-muted leading-relaxed text-base
              [&_h2]:font-display [&_h2]:text-2xl [&_h2]:md:text-3xl [&_h2]:text-ink [&_h2]:mt-12 [&_h2]:mb-5
              [&_h3]:font-display [&_h3]:text-xl [&_h3]:text-ink [&_h3]:mt-8 [&_h3]:mb-3
              [&_h4]:font-body [&_h4]:font-bold [&_h4]:text-ink [&_h4]:mt-6 [&_h4]:mb-2
              [&_p]:mb-5 [&_p]:text-base [&_p]:leading-relaxed
              [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-5 [&_ul]:space-y-1.5
              [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-5 [&_ol]:space-y-1.5
              [&_li]:text-base
              [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 [&_a]:hover:text-primary-dark [&_a]:transition-colors
              [&_strong]:text-ink [&_strong]:font-semibold
              [&_em]:italic
              [&_img]:rounded-2xl [&_img]:my-4 [&_img]:max-w-full [&_img]:h-auto [&_img]:shadow-card
              [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-5 [&_blockquote]:italic [&_blockquote]:my-6 [&_blockquote]:text-ink-muted
              [&_hr]:my-10 [&_hr]:border-primary/20
              [&_table]:w-full [&_table]:my-6 [&_table]:border-collapse
              [&_th]:text-left [&_th]:font-body [&_th]:font-bold [&_th]:text-ink [&_th]:py-3 [&_th]:px-4 [&_th]:border-b-2 [&_th]:border-primary/30
              [&_td]:py-3 [&_td]:px-4 [&_td]:border-b [&_td]:border-ink/10
            "
            dangerouslySetInnerHTML={{ __html: post.content.rendered }}
          />
        </div>
      </section>

      {/* ── Back navigation ──────────────────────────────────────────── */}
      <section className="bg-background pb-16">
        <div className="container-custom max-w-3xl">
          <div className="border-t border-primary/15 pt-10 flex justify-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-primary text-white font-body font-semibold px-6 py-3 rounded-full hover:bg-primary-dark transition-colors duration-200 shadow-glow-primary"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </section>
    </>
  )
}
